import { useState, useMemo, useCallback } from 'react';
import type { UnitConfiguration, PropertyUnit } from '@/lib/units';

interface SearchResult {
  configurations: UnitConfiguration[];
  units: PropertyUnit[];
}

export const useResidencesSearch = (
  configurations: UnitConfiguration[],
  units: PropertyUnit[],
  debounceMs: number = 300
) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query
  const updateSearch = useCallback((query: string) => {
    setSearchQuery(query);
    
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [debounceMs]);

  // Search logic
  const searchResults = useMemo((): SearchResult => {
    if (!debouncedQuery.trim()) {
      return {
        configurations,
        units,
      };
    }

    const query = debouncedQuery.toLowerCase().trim();

    // Search configurations
    const matchingConfigurations = configurations.filter((config) => {
      const nameMatch = config.name.toLowerCase().includes(query);
      const blockMatch = config.block.toLowerCase().includes(query);
      const locationMatch = config.location.toLowerCase().includes(query);
      const bedroomsMatch = config.bedrooms.toString().includes(query);
      const unitTypeMatch = config.unit_type.toLowerCase().includes(query);
      
      return nameMatch || blockMatch || locationMatch || bedroomsMatch || unitTypeMatch;
    });

    // Search units
    const matchingUnits = units.filter((unit) => {
      const unitNumberMatch = unit.unit_number.toLowerCase().includes(query);
      const blockMatch = unit.block.toLowerCase().includes(query);
      const floorMatch = unit.floor.toLowerCase().includes(query);
      const categoryMatch = unit.category.toLowerCase().includes(query);
      const viewTypeMatch = unit.view_type.toLowerCase().includes(query);
      const bedroomsMatch = unit.bedrooms.toString().includes(query);
      
      return unitNumberMatch || blockMatch || floorMatch || categoryMatch || viewTypeMatch || bedroomsMatch;
    });

    return {
      configurations: matchingConfigurations,
      units: matchingUnits,
    };
  }, [debouncedQuery, configurations, units]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setDebouncedQuery('');
  }, []);

  return {
    searchQuery,
    debouncedQuery,
    updateSearch,
    clearSearch,
    results: searchResults,
    hasResults: searchResults.configurations.length > 0 || searchResults.units.length > 0,
    isSearching: debouncedQuery.trim().length > 0,
  };
};

